import { App, View } from "@pimo/pimo-app-builder";
import { DragAndDropLayout } from "@pimo/pimo-components/src/lib/layouts/drag-and-drop-layout/drag-and-drop-layout";

import { TestAppState } from "../../app";
import { addCategoryLevelAndItemsOverTimeCards } from "./cards/category-level-and-items-over-time";
import { addCompletedProjectsLineChartCards } from "./cards/completed-projects-line-charts";
import { addCompletionAndCategoryBreakdownCards } from "./cards/completion-and-category-breakdown";
import { addDueItemsAndProjectsCards } from "./cards/due-items-and-projects";
import { addItemCompletionCountsAndSemaphoreStatusCards } from "./cards/item-completion-counts-and-semaphore-status";
import { addItemCountCards } from "./cards/item-counts";
import { addProgramDescriptionAndManagementSummaryCards } from "./cards/program-description-and-management-summary";
import { addProgressAndBudgetCards } from "./cards/progress-and-budget";
import { addProjectCountCards } from "./cards/project-counts";
import { addProjectStatusDonutAndCountCards } from "./cards/project-status-donuts-and-counts";
import { addSemaphoreStatusAndProjectCompletionCountCards } from "./cards/semaphore-status-and-project-completion-counts";
import { addStatusAndCompletionCards } from "./cards/status-and-completion";
import { addStatusOfItemsAndTrackingCompletionCards } from "./cards/status-of-items-and-tracking-completion";
import { addTeamAndDurationCards } from "./cards/team-and-duration";
import { addTitleCard } from "./cards/title";

type OverviewCard = (
  view: View<TestAppState, DragAndDropLayout>,
  app: App<TestAppState>
) => void;

/**
 * Every card the products overview page is made of, **in the order they are
 * added**, split out of the 3,600-line `buildOverview()` monolith (#2222) —
 * the same way `report-plugin.tsx`'s `buildReportView()` was split.
 *
 * The order is the point of this array: the drag-and-drop layout places
 * cards by the sequence in which they reach the view, and
 * `overview-plugin.structure.spec.ts` asserts the exact card order, count,
 * and `componentId` values this array produces — including two cards that
 * register with no id at add-time and one deliberate duplicate id (see that
 * spec's own `CHARACTERIZATION` tests). None of that is fixed here; the
 * point of this split is that nothing observable changes.
 *
 * The projects table itself is not in this list: it is added directly by
 * `OverviewPlugin`'s `buildDynamicallyBasedOnAppState` builder, after every
 * entry here.
 *
 * Cards that never touch the app declare `(view)` alone; a narrower
 * parameter list is assignable to the wider type, so they need no unused
 * second parameter to sit in this array.
 */
export const OVERVIEW_CARDS: OverviewCard[] = [
  addTitleCard,
  addProgramDescriptionAndManagementSummaryCards,
  addItemCountCards,
  addProjectCountCards,
  addStatusAndCompletionCards,
  addDueItemsAndProjectsCards,
  addTeamAndDurationCards,
  addProgressAndBudgetCards,
  addProjectStatusDonutAndCountCards,
  addSemaphoreStatusAndProjectCompletionCountCards,
  addItemCompletionCountsAndSemaphoreStatusCards,
  addStatusOfItemsAndTrackingCompletionCards,
  addCompletionAndCategoryBreakdownCards,
  addCategoryLevelAndItemsOverTimeCards,
  addCompletedProjectsLineChartCards,
];
